import { useState } from 'react'
import { CHAINS } from '../constants'
import { formatAddress } from '../utils'
import type { Hex, Address } from 'viem'

interface SuccessConfirmationProps {
  txHash: Hex
  controllerAddress: Address
  chainId: number
  onClose: () => void
}

export function SuccessConfirmation({
  txHash,
  controllerAddress,
  chainId,
  onClose,
}: SuccessConfirmationProps) {
  const [copied, setCopied] = useState<string | null>(null)

  const getExplorerUrl = () => {
    if (chainId === 42) {
      return `${CHAINS.lukso.blockExplorers.default.url}/tx/${txHash}`
    } else if (chainId === 4201) {
      return `${CHAINS.luksoTestnet.blockExplorers.default.url}/tx/${txHash}`
    }
    return `https://explorer.execution.mainnet.lukso.network/tx/${txHash}`
  }

  const handleCopy = async (text: string, key: string) => {
    await navigator.clipboard.writeText(text)
    setCopied(key)
    setTimeout(() => setCopied(null), 2000)
  }

  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4 z-50">
      <div className="w-full max-w-md rounded-xl border border-white/5 bg-surface shadow-2xl overflow-hidden">
        {/* Success header */}
        <div className="px-6 py-6 border-b border-white/10 text-center">
          <div className="w-14 h-14 mx-auto rounded-full bg-green-500/20 border border-green-500/40 flex items-center justify-center mb-3">
            <svg className="w-8 h-8 text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h2 className="text-xl font-semibold text-gray-100">
            Permissions updated!
          </h2>
        </div>

        {/* Details */}
        <div className="p-6 space-y-4">
          {/* Controller address */}
          <div>
            <label className="text-xs text-gray-500 mb-1.5 block">Controller Address</label>
            <div className="flex items-center gap-2 rounded-xl border border-white/[0.08] bg-white/[0.04] px-3 py-2.5">
              <code className="flex-1 font-mono text-sm text-gray-200 truncate">
                {formatAddress(controllerAddress, 8)}
              </code>
              <button
                onClick={() => handleCopy(controllerAddress, 'controller')}
                className="p-1.5 rounded-lg text-gray-400 hover:text-gray-200 hover:bg-white/10 transition-colors"
              >
                {copied === 'controller' ? (
                  <svg className="w-4 h-4 text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                ) : (
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2m0 0h2a2 2 0 012 2v3m2 4H10m0 0l3-3m-3 3l3 3" />
                  </svg>
                )}
              </button>
            </div>
          </div>

          {/* Transaction hash */}
          <div>
            <label className="text-xs text-gray-500 mb-1.5 block">Transaction Hash</label>
            <div className="flex items-center gap-2 rounded-xl border border-white/[0.08] bg-white/[0.04] px-3 py-2.5">
              <code className="flex-1 font-mono text-sm text-gray-200 truncate">
                {formatAddress(txHash, 8)}
              </code>
              <button
                onClick={() => handleCopy(txHash, 'tx')}
                className="p-1.5 rounded-lg text-gray-400 hover:text-gray-200 hover:bg-white/10 transition-colors"
              >
                {copied === 'tx' ? (
                  <svg className="w-4 h-4 text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                ) : (
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2m0 0h2a2 2 0 012 2v3m2 4H10m0 0l3-3m-3 3l3 3" />
                  </svg>
                )}
              </button>
            </div>
          </div>

          {/* Done primary, View on Explorer secondary below */}
          <div className="flex flex-col gap-2 pt-2">
            <button onClick={onClose} className="btn-primary w-full">
              Done
            </button>
            <a
              href={getExplorerUrl()}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-secondary flex items-center justify-center gap-2 w-full"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
              </svg>
              View on Explorer
            </a>
          </div>
        </div>
      </div>
    </div>
  )
}
